import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getReconSessions, getReconSession, uploadBankStatement, autoMatchSession, reconcileLine } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Upload, Check, X, ArrowsClockwise, MagnifyingGlass, Link as LinkIcon } from '@phosphor-icons/react';

export default function ReconciliationPage() {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [matching, setMatching] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  useEffect(() => { fetchSessions(); }, []);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const res = await getReconSessions();
      setSessions(res.data.sessions);
    } catch (e) { toast.error('Failed to load sessions'); }
    finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadBankStatement(file);
      toast.success(`Uploaded ${res.data.total_lines} lines`);
      setIsUploadOpen(false);
      setSelectedSession(res.data);
      fetchSessions();
    } catch (e) { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleSelectSession = async (sessionId) => {
    try {
      const res = await getReconSession(sessionId);
      setSelectedSession(res.data);
    } catch (e) { toast.error('Failed to load session'); }
  };

  const handleAutoMatch = async () => {
    if (!selectedSession) return;
    setMatching(true);
    try {
      const res = await autoMatchSession(selectedSession.session_id);
      toast.success(`Matched ${res.data.matched} of ${res.data.total} lines`);
      handleSelectSession(selectedSession.session_id);
    } catch (e) { toast.error('Auto-match failed'); }
    finally { setMatching(false); }
  };

  const handleReconcile = async (lineId, transactionId) => {
    try {
      await reconcileLine(selectedSession.session_id, { line_id: lineId, transaction_id: transactionId });
      toast.success('Line reconciled');
      handleSelectSession(selectedSession.session_id);
    } catch (e) { toast.error('Failed to reconcile'); }
  };

  const lines = selectedSession?.lines || [];
  const matched = lines.filter(l => l.status === 'matched').length;
  const reconciled = lines.filter(l => l.status === 'reconciled').length;
  const unmatched = lines.filter(l => l.status === 'unmatched').length;

  if (loading) {
    return (<Layout title="Reconciliation"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin"></div></div></Layout>);
  }

  return (
    <Layout title="Reconciliation">
      <div data-testid="reconciliation-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <p className="text-sm text-zinc-600">Upload bank statements, auto-match transactions, and reconcile discrepancies.</p>
          <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
            <DialogTrigger asChild>
              <Button className="bg-zinc-950 text-white rounded-sm" data-testid="upload-statement-button">
                <Upload size={16} className="mr-2" /> Upload Statement
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="upload-statement-dialog">
              <DialogHeader><DialogTitle>Upload Bank Statement</DialogTitle></DialogHeader>
              <div className="py-4">
                <p className="text-sm text-zinc-600 mb-4">Upload a CSV with columns: date, description, amount</p>
                <Input type="file" accept=".csv" onChange={handleUpload} disabled={uploading} className="cursor-pointer" data-testid="statement-file-input" />
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-4 gap-6 h-[calc(100vh-14rem)]">
          {/* Sessions List */}
          <div className="col-span-1 bg-white border border-zinc-200 rounded-sm overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50">
              <p className="text-xs uppercase tracking-widest text-zinc-500">{sessions.length} Sessions</p>
            </div>
            <div className="flex-1 overflow-auto">
              {sessions.map((s) => (
                <button key={s.session_id} onClick={() => handleSelectSession(s.session_id)} data-testid={`session-${s.session_id}`}
                  className={`w-full text-left px-4 py-3 border-b border-zinc-100 transition-colors ${selectedSession?.session_id === s.session_id ? 'bg-zinc-100' : 'hover:bg-zinc-50'}`}>
                  <p className="text-sm font-medium truncate">{s.filename}</p>
                  <p className="text-xs text-zinc-500">{s.total_lines} lines - {s.status}</p>
                </button>
              ))}
              {sessions.length === 0 && <p className="p-4 text-sm text-zinc-400 text-center">No sessions yet</p>}
            </div>
          </div>

          {/* Detail */}
          <div className="col-span-3 bg-white border border-zinc-200 rounded-sm overflow-hidden flex flex-col">
            {selectedSession ? (
              <>
                <div className="px-6 py-4 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">{selectedSession.filename}</h3>
                    <p className="text-xs text-zinc-500">{lines.length} lines</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex gap-3 text-xs">
                      <span className="px-2 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm">Reconciled: {reconciled}</span>
                      <span className="px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-sm">Matched: {matched}</span>
                      <span className="px-2 py-1 bg-red-50 text-red-700 border border-red-200 rounded-sm">Unmatched: {unmatched}</span>
                    </div>
                    <Button variant="outline" onClick={handleAutoMatch} disabled={matching} className="rounded-sm" data-testid="auto-match-button">
                      <ArrowsClockwise size={16} className="mr-2" /> {matching ? 'Matching...' : 'Auto-Match'}
                    </Button>
                  </div>
                </div>
                <div className="flex-1 overflow-auto">
                  <table className="w-full table-dense">
                    <thead>
                      <tr className="text-left">
                        <th className="px-4 pt-3">Status</th>
                        <th className="px-4 pt-3">Date</th>
                        <th className="px-4 pt-3">Description</th>
                        <th className="px-4 pt-3 text-right">Amount</th>
                        <th className="px-4 pt-3">Match</th>
                        <th className="px-4 pt-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line) => (
                        <tr key={line.line_id} className={`hover:bg-zinc-50 ${line.status === 'reconciled' ? 'bg-emerald-50/30' : line.status === 'unmatched' ? 'bg-red-50/30' : ''}`}>
                          <td className="px-4">
                            <span className={`px-2 py-0.5 text-xs uppercase border rounded-sm ${
                              line.status === 'reconciled' ? 'bg-emerald-50 text-emerald-700 border-emerald-500' :
                              line.status === 'matched' ? 'bg-blue-50 text-blue-700 border-blue-500' :
                              'bg-red-50 text-red-700 border-red-500'
                            }`}>{line.status}</span>
                          </td>
                          <td className="px-4 font-mono text-sm">{line.date}</td>
                          <td className="px-4 text-sm truncate max-w-xs">{line.description}</td>
                          <td className="px-4 text-right font-mono text-sm">${Math.abs(line.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                          <td className="px-4 text-xs">
                            {line.matched_transaction_id ? (
                              <span className="flex items-center gap-1 text-blue-600"><LinkIcon size={12} />{line.matched_vendor || line.matched_transaction_id.slice(0, 12)}</span>
                            ) : <span className="text-zinc-400">—</span>}
                          </td>
                          <td className="px-4">
                            {line.status === 'matched' && (
                              <button onClick={() => handleReconcile(line.line_id, line.matched_transaction_id)}
                                className="p-1.5 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 rounded-sm" title="Reconcile" data-testid={`reconcile-${line.line_id}`}>
                                <Check size={14} />
                              </button>
                            )}
                            {line.status === 'unmatched' && (
                              <span className="text-xs text-red-500">No match</span>
                            )}
                            {line.status === 'reconciled' && (
                              <Check size={14} className="text-emerald-500" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-zinc-500">
                <div className="text-center">
                  <ArrowsClockwise size={48} className="mx-auto mb-4 text-zinc-300" />
                  <p className="font-medium">Upload a bank statement to start reconciling</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
